import React, { memo, useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import Select from 'react-select'

import { useEffectOnce, useToggle } from 'root/hooks'
import { FiltersIcon, ResetFiltersIcon } from 'root/icons'
import BrownButton from 'root/components/Buttons/BrownButton'
import { toClassName } from 'root/constants'
import Button from 'root/components/Buttons/Button'
import CheckboxInput from 'root/modules/login/pages/Login/components/CheckboxInput'

import './styles.css'

import {
  CERTIFICATE_TYPES,
  CLARITY_STEPS,
  COLOR_OPTIONS,
  COLOR_STEPS,
  FLUORESCENCE_STEPS,
  INTENSITY_OPTIONS,
  OVERTONE_OPTIONS,
  QUALITY_STEPS,
  TECHNOLOGY_TYPES,
} from 'root/modules/products/constants'

import ShapeFilter from './components/ShapeFilter'
import CheckboxGroup from './components/CheckboxGroup'
import CatalogSlider from './components/CatalogSlider'

const getSliderProps = (filters, key, steps, limitsByField) => {
  const [min, max] = limitsByField
    ? limitsByField[key] ?? [filters[`${key}fr`] ?? 0, filters[`${key}to`] ?? 100]
    : [undefined, undefined]
  const [from, to] = steps
    ? filters[key]
      ? [filters[key][0] - 1, filters[key][filters[key].length - 1]]
      : [0, steps.length]
    : [filters[`${key}fr`] ?? min, filters[`${key}to`] ?? max]
  return { from, to, min, max, steps, filterKey: key }
}

const ProductFilters = ({ filters, updateFilters, resetFilters }) => {
  const [showAdvanced, toggleShowAdvanced] = useToggle()
  const [{ suppliersById, suppliers, locationById, location, limitsByField }, setFilterOptions] = useState({
    limitsByField: {},
  })

  useEffectOnce(() => {
    Promise.resolve(
      JSON.parse(
        '{"diamondFields":{"culet":["N/A","Very Small","Small","Medium","Slightly Large","Large","Very Large","Extremely Large","Pointed"],"shape":{"1":"Round","2":"Princess","3":"Pear","4":"Marquise","5":"Radiant","6":"Emerald","7":"Oval","8":"Heart","9":"Cushion","10":"Asscher","11":"Baguette","12":"Rose","99":"Other"},"colorType":{"1":"White","2":"Fancy"},"fancyColors":{"1":"Yellow","2":"Pink","3":"Blue","4":"Green","5":"Red","6":"Purple","7":"Orange","8":"Violet","9":"Gray","10":"Black","11":"Brown","12":"Champagne","13":"Cognac","14":"Chameleon","99":"Other"},"whiteColors":["J","I","H","G","F","E","D"],"fancyIntensity":{"1":"Faint","2":"Very light","3":"Light","4":"Fancy light","5":"Fancy","6":"Fancy dark","7":"Fancy intense","8":"Fancy vivid","9":"Fancy deep","99":"Other"},"fancyOvertone":{"1":"Red","2":"Orangey Red","3":"Reddish Orange","4":"Pink","5":"Pinkish Orange","6":"Orange","7":"Yellowish Orange","8":"Yellow Orange","9":"Orangey Yellow","10":"Yellow","11":"Yellow Brown","12":"Greenish Yellow","13":"Green Yellow","14":"Yellow Green","15":"Yellowish Green","16":"Brown Greenish Yellow","17":"Gray Greenish Yellow","18":"Gray Yellowish Green ","19":"Gray Green","20":"Green","21":"Bluish Green","22":"Blue Green","23":"Green Blue","24":"Greenish Blue","25":"Blue","26":"Violetish Blue","27":"Bluish Violet","28":"Violet","29":"Purple","30":"Reddish Purple","31":"Red Purple","32":"Purple red","33":"Purplish Red","34":"Pinkish Purple","35":"Pink Purple","36":"Purple Pink","37":"Purplish Pink","38":"Brown Pink","39":"Pink Brown","40":"Pinkish Brown","41":"Light Brown","42":"Champagne","43":"Cognac","44":"Chameleon","45":"Gray","46":"Grayish","47":"Black","48":"White","99":"Other"},"clarity":["SI2","SI1","VS2","VS1","VVS2","VVS1","IF","FL"],"cut":{"1":"ID","2":"EX","3":"VG","4":"GD","5":"FR","6":"PR"},"certificate":{"1":"GIA","2":"IGI","3":"HRD","4":"GCAL","5":"AGS"},"polish":{"1":"ID","2":"EX","3":"VG","4":"GD","5":"FR","6":"PR"},"symmetry":{"1":"ID","2":"EX","3":"VG","4":"GD","5":"FR","6":"PR"},"fluorescence":["Very strong","Strong","Medium","Faint","NONE"],"girdle":["N/A","Extremely Thin","Very Thin","Thin","Medium","Slightly Thick","Thick","Very Thick","Extremely Thick","Faceted","Smooth"],"grownTechnology":{"1":"CVD","2":"HPHT"}},"priceMin":"3.24","priceMax":"80340","caratMin":"0.51","caratMax":"12","measurementsZMin":0,"measurementsZMax":255,"measurementsXMin":0,"measurementsXMax":255,"measurementsYMin":0,"measurementsYMax":255,"tableSizeMin":0,"tableSizeMax":100,"totalDepthMin":0,"totalDepthMax":100,"suppliers":[{"label":"LGDeal LLC","fullUrl":null,"urlBrick":null,"urlParam":"s","id":"58zAQeAXwm"},{"label":"Yorso","fullUrl":null,"urlBrick":null,"urlParam":"s","id":"MEmO85YZVb"},{"label":"DSEM LLC","fullUrl":null,"urlBrick":null,"urlParam":"s","id":"JkxYVjYd3Z"},{"label":"SDF Inc.","fullUrl":null,"urlBrick":null,"urlParam":"s","id":"p6oO62OQ9m"}],"location":{"33":{"code":"IO","name":"{\\"en\\": \\"British Indian Ocean Territory\\"}","fullCode":"IOT","numCode":"086","phoneCode":"246","worldPart":"0","currency":"USD","forceUpdate":false},"109":{"code":"IN","name":"{\\"en\\": \\"India\\"}","fullCode":"IND","numCode":"356","phoneCode":"91","worldPart":"0","currency":"INR","forceUpdate":false},"209":{"code":"ES","name":"{\\"en\\": \\"Spain\\"}","fullCode":"ESP","numCode":"724","phoneCode":"34","worldPart":"0","currency":"EUR","forceUpdate":false},"245":{"code":"US","name":"{\\"en\\": \\"United States\\"}","fullCode":"USA","numCode":"840","phoneCode":"1","worldPart":"0","currency":"USD","forceUpdate":false}}}',
      ),
    ).then((options) => {
      Object.entries(options.location).forEach(([id, item]) =>
        Object.assign(item, { name: JSON.parse(item.name).en, id }),
      )
      setFilterOptions({
        locationById: options.location,
        location: Object.values(options.location),
        suppliers: options.suppliers,
        suppliersById: options.suppliers.reduce((acc, item) => Object.assign(acc, { [item.id]: item }), {}),
        limitsByField: {
          p: [Math.floor(parseFloat(options.priceMin)), Math.ceil(parseFloat(options.priceMax))],
          c: [options.caratMin, options.caratMax].map(parseFloat),
          x: [options.measurementsXMin, options.measurementsXMax],
          y: [options.measurementsYMin, options.measurementsYMax],
          z: [options.measurementsZMin, options.measurementsZMax],
          tb: [options.tableSizeMin, options.tableSizeMax],
          dp: [options.totalDepthMin, options.totalDepthMax],
        },
      })
    })
  })

  const threeX = [filters.po, filters.sy, filters.cu].every((values) => values?.every((value) => value <= 2))

  const onThreeXClick = useCallback(() => {
    const value = !threeX ? [1, 2] : undefined
    updateFilters({ po: [0, 2], sy: [0, 2], cu: [0, 2] })
    updateFilters({ po: value, sy: value, cu: value })
  }, [threeX, updateFilters])

  return (
    <table>
      <tbody>
        <tr>
          <td>
            <div className="flex flex-col justify-between">
              <div className="pb-4 font-semibold font-itf text-brown-dim">SHAPE</div>
              <ShapeFilter value={filters.sh} updateFilters={updateFilters} />
            </div>
          </td>
          <td className="w-1/3 pl-4 pr-2">
            <div className="pb-4 font-semibold font-itf text-brown-dim">PRICE</div>
            <CatalogSlider {...getSliderProps(filters, 'p', undefined, limitsByField)} updateFilters={updateFilters} />
          </td>
          <td className="w-1/3 pl-2">
            <div className="pb-4 font-semibold font-itf text-brown-dim">CARAT</div>
            <CatalogSlider
              step={0.01}
              {...getSliderProps(filters, 'c', undefined, limitsByField)}
              updateFilters={updateFilters}
            />
          </td>
        </tr>
        <tr>
          <td className="pt-10">
            <div className="flex" style={{ minHeight: 82 }}>
              <div className="mr-6 text-xs flex flex-col justify-between">
                <BrownButton
                  style={{ fontWeight: 'normal', height: 34 }}
                  onClick={() => updateFilters({ cot: undefined })}
                  type={'button'}
                  label={'product.labels.white'}
                  className={toClassName(['w-6', filters.cot && 'bg-brown-dim-light'])}
                />
                <BrownButton
                  style={{ fontWeight: 'normal', height: 34 }}
                  onClick={() => updateFilters({ cot: 1 })}
                  type={'button'}
                  label={'product.labels.fancy'}
                  className={toClassName(['w-6', !filters.cot && 'bg-brown-dim-light'])}
                />
              </div>
              {!filters.cot ? (
                <div className="flex-1">
                  <div className="pb-4 font-semibold font-itf text-brown-dim">COLOR</div>
                  <CatalogSlider {...getSliderProps(filters, 'covw', COLOR_STEPS)} updateFilters={updateFilters} />
                </div>
              ) : (
                <>
                  <div className="flex-1 mr-2">
                    <div className="pb-2 font-semibold font-itf text-brown-dim">INTENSITY</div>
                    <Select
                      classNamePrefix="react-select2"
                      className="text-xs"
                      isClearable
                      getOptionValue={({ title }) => title}
                      getOptionLabel={({ title }) => title}
                      options={INTENSITY_OPTIONS}
                      value={filters.fin ? INTENSITY_OPTIONS[filters.fin - 1] : null}
                      onChange={(item) => updateFilters({ fin: item && item.index + 1 })}
                    />
                  </div>
                  <div className="flex-1 mr-2">
                    <div className="pb-2 font-semibold font-itf text-brown-dim">OVERTONE</div>
                    <Select
                      classNamePrefix="react-select2"
                      className="text-xs"
                      isClearable
                      getOptionValue={({ title }) => title}
                      getOptionLabel={({ title }) => title}
                      options={OVERTONE_OPTIONS}
                      value={filters.fov ? OVERTONE_OPTIONS[filters.fov - 1] : null}
                      onChange={(item) => updateFilters({ fov: item && item.index + 1 })}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="pb-2 font-semibold font-itf text-brown-dim">COLOR</div>
                    <Select
                      classNamePrefix="react-select2"
                      className="text-xs"
                      isClearable
                      getOptionValue={({ title }) => title}
                      getOptionLabel={({ title }) => title}
                      options={COLOR_OPTIONS}
                      value={filters.covf ? COLOR_OPTIONS[filters.covf - 1] : null}
                      onChange={(item) => updateFilters({ covf: item && item.index + 1 })}
                    />
                  </div>
                </>
              )}
            </div>
          </td>
          <td className="pl-4 pr-2 pt-10">
            <div className="pb-4 font-semibold font-itf text-brown-dim">CLARITY</div>
            <CatalogSlider {...getSliderProps(filters, 'cl', CLARITY_STEPS)} updateFilters={updateFilters} />
          </td>
          <td className="pl-2 pt-10">
            <div className="pb-4 font-semibold font-itf text-brown-dim">CUT</div>
            <CatalogSlider {...getSliderProps(filters, 'cu', QUALITY_STEPS)} updateFilters={updateFilters} />
          </td>
        </tr>
        <tr>
          <td className="pt-10">
            <div className="pb-4 font-semibold font-itf text-brown-dim">LOCATION</div>
            <Select
              classNamePrefix="react-select2"
              className="text-xs"
              isClearable
              getOptionValue={({ id }) => id}
              getOptionLabel={({ name }) => name}
              options={location}
              value={filters.loc && locationById ? locationById[filters.loc] : null}
              onChange={(item) => updateFilters({ loc: item && item.id })}
            />
          </td>
          <td className="pl-4 pr-2 pt-10 align-top">
            <div className="flex">
              <div className="flex items-center checkbox-input mr-8">
                <label htmlFor="photo" className="font-semibold font-itf text-brown-dim mr-2 cursor-pointer">
                  PHOTO
                </label>
                <CheckboxInput
                  id="photo"
                  value={filters.ph}
                  onChange={(ph) => updateFilters({ ph: ph ? 1 : undefined })}
                />
              </div>
              <div className="flex items-center checkbox-input">
                <label htmlFor="3ex" className="font-semibold font-itf text-brown-dim mr-2 cursor-pointer">
                  3EX
                </label>
                <CheckboxInput id="3ex" value={threeX} onChange={onThreeXClick} />
              </div>
            </div>
          </td>
          <td className="pl-2 pt-10">
            <Button className="bg-brown-dim-light " type="button" onClick={resetFilters}>
              <ResetFiltersIcon className="mr-2" />
              <span className="font-normal">{'Reset filters'}</span>
            </Button>
          </td>
        </tr>
        <tr>
          <td className="pt-10">
            <span className="text-sm cursor-pointer text-gray-600 hover:text-brown-dim" onClick={toggleShowAdvanced}>
              {'Advanced filters'}
              <FiltersIcon className="inline ml-4 text-brown-dim-light" />
            </span>
          </td>
        </tr>
        {showAdvanced && (
          <>
            <tr>
              <td className="pt-10">
                <div className="flex items-center">
                  <div className="font-semibold font-itf text-brown-dim mr-2">CERTIFICATE</div>
                  <CheckboxGroup
                    items={CERTIFICATE_TYPES}
                    values={filters.ce}
                    onChange={(ce) => updateFilters({ ce })}
                  />
                </div>
              </td>
              <td className="pl-4 pr-2 pt-10">
                <div className="pb-4 font-semibold font-itf text-brown-dim">POLISH</div>
                <CatalogSlider {...getSliderProps(filters, 'po', QUALITY_STEPS)} updateFilters={updateFilters} />
              </td>
              <td className="pl-2 pt-10">
                <div className="pb-4 font-semibold font-itf text-brown-dim">SYMMETRY</div>
                <CatalogSlider {...getSliderProps(filters, 'sy', QUALITY_STEPS)} updateFilters={updateFilters} />
              </td>
            </tr>
            <tr className="mt-10">
              <td className="pt-10">
                <div className="pb-4 font-semibold font-itf text-brown-dim">LENGTH</div>
                <CatalogSlider
                  {...getSliderProps(filters, 'z', undefined, limitsByField)}
                  postfix={'mm'}
                  updateFilters={updateFilters}
                />
              </td>
              <td className="pl-4 pr-2 pt-10">
                <div className="pb-4 font-semibold font-itf text-brown-dim">WIDTH</div>
                <CatalogSlider
                  {...getSliderProps(filters, 'x', undefined, limitsByField)}
                  postfix={'mm'}
                  updateFilters={updateFilters}
                />
              </td>
              <td className="pl-2 pt-10">
                <div className="pb-4 font-semibold font-itf text-brown-dim">HEIGHT</div>
                <CatalogSlider
                  {...getSliderProps(filters, 'y', undefined, limitsByField)}
                  postfix={'mm'}
                  updateFilters={updateFilters}
                />
              </td>
            </tr>
            <tr>
              <td className="pt-10">
                <div className="pb-4 font-semibold font-itf text-brown-dim">TABLE SIZE</div>
                <CatalogSlider
                  {...getSliderProps(filters, 'tb', undefined, limitsByField)}
                  postfix="%"
                  updateFilters={updateFilters}
                />
              </td>
              <td className="pl-4 pr-2 pt-10">
                <div className="pb-4 font-semibold font-itf text-brown-dim">TOTAL DEPTH</div>
                <CatalogSlider
                  {...getSliderProps(filters, 'dp', undefined, limitsByField)}
                  postfix="%"
                  updateFilters={updateFilters}
                />
              </td>
              <td className="pl-2 pt-10">
                <div className="pb-4 font-semibold font-itf text-brown-dim">FLUORESCENCE</div>
                <CatalogSlider {...getSliderProps(filters, 'fl', FLUORESCENCE_STEPS)} updateFilters={updateFilters} />
              </td>
            </tr>
            <tr>
              <td className="pt-10">
                <div className="flex items-center checkbox-input mr-8">
                  <label htmlFor="heartsAndArrows" className="font-semibold font-itf text-brown-dim mr-2">
                    HEARTS & ARROWS
                  </label>
                  <CheckboxInput
                    id="heartsAndArrows"
                    value={filters.haa}
                    onChange={(haa) => updateFilters({ haa: haa ? 1 : undefined })}
                  />
                </div>
              </td>
              <td className="pl-4 pr-2 pt-10">
                <div className="flex items-center">
                  <div className="font-semibold font-itf text-brown-dim mr-2">GROWN TECHNOLOGY</div>
                  <CheckboxGroup
                    items={TECHNOLOGY_TYPES}
                    values={filters.gt}
                    onChange={(gt) => updateFilters({ gt })}
                  />
                </div>
              </td>
              <td className="pl-2 pt-10">
                <div className="flex">
                  <div className="font-semibold font-itf text-brown-dim mr-8">SUPPLIER</div>
                  <Select
                    classNamePrefix="react-select2"
                    className="text-xs w-full"
                    isClearable
                    getOptionValue={({ id }) => id}
                    getOptionLabel={({ label }) => label}
                    options={suppliers}
                    value={filters.s && suppliersById ? suppliersById[filters.s] : null}
                    onChange={(item) => updateFilters({ s: item && item.id })}
                  />
                </div>
              </td>
            </tr>
          </>
        )}
      </tbody>
    </table>
  )
}

ProductFilters.propTypes = {
  filters: PropTypes.object.isRequired,
  updateFilters: PropTypes.func.isRequired,
  resetFilters: PropTypes.func.isRequired,
}

export default memo(ProductFilters)
